import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { IconEdit, IconDelete } from '../icons';
import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import { collection, query, where, getDocs, doc, updateDoc, deleteDoc, setDoc, getDoc, orderBy, addDoc, Timestamp, DocumentReference, WithFieldValue, writeBatch } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { mapErrorToUserMessage } from '../utils/userErrorMessage';
import { Buyer } from '../types/invoiceTypes';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import DataTable, { Column } from '../components/common/DataTable';
import { RowAction } from '../components/common/RowAction';
import { applyListMutations, upsertById } from '../utils/listMutationLog';
import { useListMutationLog } from '../hooks/useListMutationLog';

interface Customer extends Buyer {
  docId: string;  // NEU: Firebase Document ID zum Identifizieren in Firestore
  id?: string | null; // Die vom Kunden definierte Käufer-ID (von Buyer geerbt)
  createdAt?: any; // Firestore Timestamp
  updatedAt?: any; // Firestore Timestamp
  // Zusätzliche Kommunikationsfelder - diese sind jetzt Teil von Buyer und werden überschrieben
  // email?: string;
  // phone?: string;
  // website?: string;
}

/** Identität eines Kunden. Modulebene ⇒ stabile Referenz für Hook-Dependencies. */
const getCustomerId = (customer: Customer): string => customer.docId;

/**
 * Fügt Kunden **idempotent** in eine bestehende Liste ein: ein vorhandener
 * Eintrag mit derselben `docId` wird an Ort und Stelle ERSETZT, nur wirklich
 * neue Einträge werden angehängt.
 *
 * WARUM DAS NICHT `[...prev, neu]` SEIN DARF (Nightly-Fehlschlag
 * `e2e/customers.spec.ts`, Lauf 32631524403):
 *
 * `await setDoc(...)` löst auf, wenn der Server die Schreiboperation
 * BESTÄTIGT — nicht, wenn er sie ausgeführt hat. Zwischen "ausgeführt" und
 * "bestätigt" liegt auf einem langsamen Runner rund eine Sekunde (im
 * Reproduktionslauf: Klick +2176 ms, Server hat den Datensatz +2600 ms,
 * Promise löst +4695 ms auf — dazwischen brach die Write-Backchannel-
 * Verbindung ab und das SDK schickte den Schreibvorgang erneut).
 *
 * Der Erst-Ladevorgang der Seite läuft zu diesem Zeitpunkt noch. Landet seine
 * `getDocs`-Antwort in genau diesem Fenster (im Lauf: +3383 ms), enthält sie
 * den neuen Kunden bereits — die Antwort ist weder überholt noch "vor der
 * Mutation gestartet" (der damalige zweite Zähler `mutationGenerationRef` ist
 * inzwischen durch das Mutations-Log ersetzt, aber die Zeitfolge ist dieselbe:
 * die Mutation wird erst NACH dem `await` angemeldet, das Log ist zum
 * Antwort-Zeitpunkt also noch leer). Kein Mechanismus greift hier zu Recht.
 * Danach hängt das optimistische Update denselben Kunden ein ZWEITES Mal an:
 * zwei Einträge mit IDENTISCHER `docId`.
 *
 * Die Folge ist nicht bloß eine doppelte Zeile. `DataTable` benutzt `docId`
 * als React-`key`; zwei Kinder mit demselben Key sind ein undefinierter
 * Reconciliation-Zustand, und beim späteren Entfernen bleibt eines der beiden
 * `<tr>` als verwaistes DOM-Element stehen — dauerhaft, obwohl der State
 * korrekt ist (im Lauf gemessen: `customers.length === 1121` ohne Treffer,
 * DOM 1122 Zeilen mit Treffer). Genau das ist der "gelöscht, aber die Zeile
 * bleibt stehen"-Endzustand aus dem Nightly.
 *
 * Deshalb ist jedes optimistische Einfügen ein Upsert. Es ist in beiden
 * Reihenfolgen korrekt: kam die Antwort schon, wird ersetzt; kam sie noch
 * nicht, wird angehängt.
 *
 * Die Semantik selbst liegt seit der Merge-Welle in `utils/listMutationLog`
 * (`upsertById`), damit Kunden- und Produktliste dieselbe Idempotenz-Regel
 * benutzen. Diese Funktion bleibt als Ein-Zeilen-Wrapper exportiert: sie ist
 * mit ihren 11 Testfällen der Regressionsschutz dafür, dass die generische
 * Fassung semantisch identisch geblieben ist.
 */
export function upsertCustomers(list: Customer[], incoming: Customer[]): Customer[] {
  return upsertById(list, incoming, getCustomerId);
}

const CustomerManagementPage: React.FC = () => {
  const { t } = useTranslation('customer');
  const { currentUser, effectiveScopeUid } = useAuth();
  // M11b-3: advisor (read-only) sieht keine Kunden-CRUD-Aktionen
  // (Neu/Import/Bearbeiten/Löschen). Die Kundenliste (Lesevorgang) bleibt sichtbar.
  const isReadOnly = useIsReadOnly();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importData, setImportData] = useState('');
  const [importFileName, setImportFileName] = useState<string | null>(null);
  const [useFileUpload, setUseFileUpload] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Für den Lösch-Bestätigungsdialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  
  // Für die Editierform
  const [formData, setFormData] = useState<Partial<Customer>>({});

  // ── Nebenläufigkeit: Generationen-Zähler + Mutations-Log ───────────────────
  //
  // `loadGenerationRef` zählt bei jedem `fetchCustomers`-Start hoch (Muster wie
  // InboxPage.tsx): eine Antwort, deren Generation nicht mehr passt, ist von
  // einer neueren Anfrage ÜBERHOLT und darf weder `customers` noch `loading`
  // noch `error` schreiben. Dieses Hausmuster bleibt unverändert — eine
  // überholte Antwort wird VERWORFEN, nicht gemergt: die neuere Antwort ist in
  // jeder Hinsicht besser informiert.
  //
  // Das Mutations-Log ersetzt den früher hier stehenden zweiten Zähler
  // (`mutationGenerationRef`). Der beantwortete die Frage "ist diese Antwort
  // veraltet?" mit ja/nein und verwarf im Ja-Fall die KOMPLETTE Serverantwort.
  // Das ist harmlos, solange die Liste schon gefüllt war — und ein sichtbarer
  // Datenverlust, wenn die verworfene Antwort die ERSTLADUNG war: dann bleibt
  // nur der gerade lokal angelegte Kunde übrig, bei 1000+ Kunden bis zum
  // Reload. Die Kopfleiste mit "Neuer Kunde"/"Import" liegt außerhalb des
  // Lade-Zweigs, dieser Pfad ist während der Erstladung also erreichbar.
  //
  // Das Log beantwortet stattdessen die präzisere Frage: "welche lokalen
  // Mutationen kennt diese Antwort noch nicht?" — Serverantwort = Basis, das
  // seit Ladungsstart protokollierte lokale Delta wird darüber gelegt
  // (`applyListMutations`). Spezifikation:
  // `architecture-list-concurrency-merge.md` §3 (Fallmatrix und Konfliktzellen).
  const loadGenerationRef = useRef(0);
  const customerLog = useListMutationLog<Customer>(getCustomerId);

  // `t` wird in `fetchCustomers` ausschließlich im catch-Zweig gebraucht und ist
  // deshalb bewusst KEINE useCallback-Dependency: `src/i18n/config.ts` bindet
  // react-i18next mit `bindI18n: 'languageChanged loaded'` und
  // `bindI18nStore: 'added removed'` an 27 einzeln über HTTP nachgeladene
  // Namespaces. Jedes eintreffende Bundle erzeugt eine neue `t`-Referenz, und
  // mit `t` in den Dependencies hätte jede davon einen kompletten weiteren
  // Firestore-Read plus Lade-Spinner ausgelöst. Über das Ref bleibt die
  // Fehlermeldung trotzdem in der aktuell aktiven Sprache.
  const tRef = useRef(t);
  tRef.current = t;

  // Fetche Kunden aus der Firestore-Datenbank
  const fetchCustomers = useCallback(async () => {
    if (!currentUser) return;

    const myGen = ++loadGenerationRef.current;
    /** Startmarke im Mutations-Log: alles danach kennt dieser Snapshot nicht. */
    const myMark = customerLog.mark();
    /** Eine neuere Anfrage läuft — deren Antwort gewinnt, diese hier ist tot. */
    const isSuperseded = () => myGen !== loadGenerationRef.current;

    setLoading(true);
    setError(null);

    try {
      // Anpassen an die neue flache Struktur
      const customersRef = collection(db, 'customers');
      const q = query(customersRef, where('userId', '==', effectiveScopeUid));
      
      const querySnapshot = await getDocs(q);
      if (isSuperseded()) return;

      const fetchedCustomers: Customer[] = [];

      querySnapshot.forEach((doc) => {
        fetchedCustomers.push({
          ...doc.data() as Customer,
          docId: doc.id,  // Verwende docId für Firebase Document ID
        });
      });
      
      // Alles, was seit dem Start dieser Ladung lokal (und serverbestätigt)
      // mutiert wurde. Leeres Array ⇒ dieser Snapshot ist aktuell.
      let pending = customerLog.pendingSince(myMark);

      // Wenn keine Kunden in der "customers" Collection gefunden wurden,
      // versuchen wir, sie aus früheren Rechnungen zu extrahieren
      if (fetchedCustomers.length === 0 && !isSuperseded() && pending.length === 0) {
        // Dieser Zweig SCHREIBT (er legt Kunden aus Alt-Rechnungen an) — er ist
        // der EINZIGE Punkt, an dem der alte Doppel-Guard erhalten bleiben MUSS:
        // er schützt einen Schreibpfad, keine Anzeige. Zwei parallele Läufe
        // würden dieselben Kunden doppelt anlegen, und die Prämisse "dieser
        // Nutzer hat null Kunden" ist falsch, sobald er im Ladefenster einen
        // angelegt hat (`pending.length > 0`). Bei nicht-leerem Log wird das
        // Seeding übersprungen und der leere Server-Array als Basis genommen —
        // die Anzeige ergibt sich dann allein aus dem Log. Beim nächsten
        // Seitenaufruf wird Seeding erneut evaluiert.
        const invoicesRef = collection(db, 'invoices');
        const invoiceQuery = query(invoicesRef, where('userId', '==', effectiveScopeUid));
        const invoiceSnapshot = await getDocs(invoiceQuery);
        
        // Set zum Speichern einzigartiger Kunden basierend auf Namen/Firma
        const uniqueCustomers = new Map<string, Omit<Customer, 'docId'>>();
        
        invoiceSnapshot.forEach((doc) => {
          const invoiceData = doc.data();
          if (invoiceData?.buyer) {
            const buyer = invoiceData.buyer as Buyer;
            const key = buyer.name || '';
            
            if (key && !uniqueCustomers.has(key)) {
              // Wir speichern den Kunden ohne docId
              uniqueCustomers.set(key, buyer);
            }
          }
        });
        
        // Kunden in Firestore speichern und dann zur Liste hinzufügen
        const newCustomersPromises = Array.from(uniqueCustomers.values()).map(async (customerWithoutDocId) => {
          // Erstelle eine neue Customer-Referenz in der neuen flachen Struktur
          const newCustomerRef = doc(collection(db, 'customers'));
          await setDoc(newCustomerRef, {
            ...customerWithoutDocId,
            userId: effectiveScopeUid, // userId als Feld hinzufügen
            createdAt: new Date(),
            updatedAt: new Date(),
          });
          
          // Neues Kundenobjekt mit docId zurückgeben
          return {
            ...customerWithoutDocId,
            docId: newCustomerRef.id
          } as Customer;
        });
        
        // Warten, bis alle Kunden gespeichert wurden, und sie dann zur Liste hinzufügen
        const newCustomers = await Promise.all(newCustomersPromises);
        if (isSuperseded()) return;
        fetchedCustomers.push(...newCustomers);
        // Das Seeding hat auf einen weiteren `await` gewartet — in dieser Zeit
        // kann erneut lokal mutiert worden sein.
        pending = customerLog.pendingSince(myMark);
      }

      // Letzte Prüfung unmittelbar vor dem Schreiben: zwischen Query-Start und
      // hier kann eine neuere Anfrage dazwischengekommen sein. NUR sie darf
      // dieses Schreiben noch verhindern — eine zwischenzeitliche lokale
      // Mutation nicht mehr, die wird jetzt übergelegt statt zu verwerfen.
      if (isSuperseded()) return;

      if (customerLog.isOverflowed()) {
        // Fallback auf das historische Verhalten (Antwort verwerfen). Per
        // Definition keine Regression, praktisch unerreichbar — die Grenze
        // verhindert nur unbegrenztes Log-Wachstum und beliebig altes
        // Überschreiben frischer Serverwerte.
        customerLog.settleUpTo(myMark);
        return;
      }

      // Serverantwort = Basis, lokales Delta = Overlay.
      setCustomers(applyListMutations(fetchedCustomers, pending, getCustomerId));
      // Diese Ladung ist die jüngste (sonst wäre sie überholt) und hat gerade
      // geschrieben: alle Log-Einträge stecken im neuen State. Ältere offene
      // Ladungen sind überholt und werden nie schreiben, eine jüngere kann es
      // nicht geben. Ein Eintrag, der eine später gestartete Ladung überlebt,
      // würde deren frischeren Serverwert mit veralteter Nutzlast überschreiben.
      customerLog.settleApplied();
    } catch (err: any) {
      if (isSuperseded()) return;
      console.error('Fehler beim Laden der Kunden:', err);
      setError(mapErrorToUserMessage(err, tRef.current, { fallbackKey: 'messages.loadError' }));
      // Die Liste bleibt unangetastet (die optimistische Mutation steht bereits
      // im State). Einträge bis zur Startmarke sind in jeder künftigen
      // Serverantwort enthalten und dürfen nicht dauerhaft konserviert werden.
      customerLog.settleUpTo(myMark);
    } finally {
      // Nur die jüngste Anfrage besitzt den Lade-Zustand. Eine überholte Antwort
      // darf ihn nicht ausschalten, sonst verschwindet der Spinner, während die
      // aktuelle Anfrage noch läuft. Jede nicht überholte Antwort löst ihn auf —
      // auch die, die im Overflow-Fallback nichts geschrieben hat, sonst bliebe
      // der Spinner stehen.
      if (!isSuperseded()) setLoading(false);
    }
  }, [currentUser, effectiveScopeUid, customerLog]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);
  
  // Filtern der Kunden (Sortierung übernimmt die DataTable via Column.sortValue/initialSort,
  // M-E — ersetzt die vormals hier duplizierte requestSort/getSortIndicator-Mechanik)
  const filteredCustomers = useMemo(() => {
    if (!searchTerm) return customers;

    const lowerCaseSearch = searchTerm.toLowerCase();
    return customers.filter(customer => {
      // Suche in allen relevanten Feldern
      return (
        (customer.name?.toLowerCase().includes(lowerCaseSearch) || false) ||
        (customer.companyName?.toLowerCase().includes(lowerCaseSearch) || false) ||
        (customer.personName?.toLowerCase().includes(lowerCaseSearch) || false) ||
        (customer.street?.toLowerCase().includes(lowerCaseSearch) || false) ||
        (customer.zip?.toLowerCase().includes(lowerCaseSearch) || false) ||
        (customer.city?.toLowerCase().includes(lowerCaseSearch) || false) ||
        (customer.country?.toLowerCase().includes(lowerCaseSearch) || false) ||
        (customer.customerId?.toLowerCase().includes(lowerCaseSearch) || false)
      );
    });
  }, [customers, searchTerm]);
  
  // Customer Bearbeiten
  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData({ ...customer });
    setIsEditModalOpen(true);
  };
  
  // Customer Löschen - öffnet Bestätigungsdialog
  const handleDelete = (customer: Customer) => {
    setCustomerToDelete(customer);
    setDeleteDialogOpen(true);
  };
  
  // Tatsächliche Löschung nach Bestätigung
  const confirmDelete = async () => {
    if (!customerToDelete || !currentUser) return;
    
    try {
      await deleteDoc(doc(db, 'customers', customerToDelete.docId));
      // Protokollieren erst NACH der Server-Bestätigung (ein fehlgeschlagener
      // Schreibvorgang darf keinen Eintrag hinterlassen) und außerhalb des
      // `setState`-Updaters (StrictMode ruft Updater doppelt auf).
      customerLog.recordDelete([customerToDelete.docId]);
      setCustomers(prevCustomers => prevCustomers.filter(customer => customer.docId !== customerToDelete.docId));
      toast.success(t('messages.deleteSuccess'));
    } catch (err: any) {
      console.error('Fehler beim Löschen des Kunden:', err);
      // #32 T1: kein roher `err.message` mehr im Toast (Plan §0.2) — nur der
      // feste, bereits lokalisierte Aktions-Text.
      toast.error(t('messages.deleteError'));
    } finally {
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };
  
  // Form Änderungen
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Form speichern
  const handleSaveCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) return;

    setIsProcessing(true);
    
    try {
      // Generiere den Name-Wert, wenn nicht vorhanden
      if (!formData.name && (formData.companyName || formData.personName)) {
        formData.name = formData.companyName || formData.personName || '';
      }

      // Firestore auto-generated IDs are exactly 20 characters.
      // A temporary docId set by the "Neuer Kunde" button uses Date.now() (≈13 chars).
      // Any docId that is NOT a valid 20-char Firestore ID belongs to a new (unsaved) customer.
      const isNewCustomer = !editingCustomer || !editingCustomer.docId || editingCustomer.docId.toString().length !== 20;
      
      let customerRef: DocumentReference<Customer>;
      if (isNewCustomer) {
        // Erstellen eines neuen Kunden in der flachen Struktur
        customerRef = doc(collection(db, 'customers')) as DocumentReference<Customer>;
      } else {
        // Aktualisieren eines vorhandenen Kunden
        customerRef = doc(db, 'customers', editingCustomer.docId) as DocumentReference<Customer>;
      }
      
      // Konstruieren des Kunden-Objekts
      const customerData = {
        ...formData,
        name: formData.companyName || formData.personName || formData.name || '',
        userId: effectiveScopeUid, // userId als Feld hinzufügen
        updatedAt: new Date(),
      };

      if (isNewCustomer) {
        // Für einen neuen Kunden setzen wir die createdAt-Zeit
        customerData.createdAt = new Date();
        // Stellen wir sicher, dass docId nicht im Objekt ist, da Firestore es automatisch generiert
        delete customerData.docId;
        
        try {
          await setDoc(customerRef, customerData as WithFieldValue<Customer>);
          
          // Hinzufügen des neuen Kunden zur lokalen Liste mit der generierten docId
          const newCustomer = { ...customerData, docId: customerRef.id } as Customer;
          // Nach der Bestätigung, außerhalb des Updaters (siehe confirmDelete).
          customerLog.recordCreate([newCustomer]);
          // Upsert statt Anhängen: eine Lade-Antwort, die während des Schreibens
          // eintraf, kann den Kunden bereits enthalten (siehe upsertCustomers).
          setCustomers(prev => upsertCustomers(prev, [newCustomer]));
          toast.success(t('messages.saveSuccess'));
        } catch (err: any) {
          console.error('Fehler beim Speichern:', err);
          setError(mapErrorToUserMessage(err, t, { fallbackKey: 'messages.saveError' }));
        }
      } else {
        // Vorhandenen Kunden aktualisieren
        try {
          // Identität IMMER aus `editingCustomer` — das ist dieselbe Quelle, aus
          // der oben `customerRef` gebildet wurde. `formData.docId` ist nur eine
          // Kopie davon und kann durch das Formular verlorengehen; ein
          // abweichender Wert würde hier einen zweiten Eintrag mit fremder
          // `docId` erzeugen (doppelter React-Key, siehe upsertCustomers).
          const customerId = editingCustomer.docId;
          // docId aus dem Objekt entfernen, da es nicht aktualisiert werden soll
          const updateData = { ...customerData };
          delete updateData.docId;
          
          await updateDoc(customerRef, updateData);

          // Lokale Liste aktualisieren
          const updatedCustomer = { ...customerData, docId: customerId } as Customer;
          // `recordUpdate` (nicht `recordCreate`): enthält eine später
          // eintreffende Serverantwort diesen Kunden NICHT mehr, hat ihn ein
          // anderes Gerät gelöscht — er darf dann nicht wiederauferstehen.
          customerLog.recordUpdate([updatedCustomer]);
          setCustomers(prev => upsertCustomers(prev, [updatedCustomer]));
          toast.success(t('messages.updateSuccess'));
        } catch (err: any) {
          console.error('Fehler beim Aktualisieren:', err);
          setError(mapErrorToUserMessage(err, t, { fallbackKey: 'messages.updateError' }));
        }
      }

      setIsEditModalOpen(false);
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      setError(mapErrorToUserMessage(err, t, { fallbackKey: 'messages.saveError' }));
    } finally {
      setIsProcessing(false);
    }
  };
  
  // File-Einlesen-Handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImportFileName(file.name);
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && event.target.result) {
          setImportData(event.target.result.toString());
        }
      };
      reader.readAsText(file);
    }
  };

  // Import Handler
  const handleImport = async () => {
    if (!importData.trim() || !currentUser) return;
    
    setIsProcessing(true);
    
    try {
      let importedCustomers: Partial<Customer>[] = [];
      
      // Versuche, die Daten als JSON zu parsen
      try {
        importedCustomers = JSON.parse(importData);
        if (!Array.isArray(importedCustomers)) {
          importedCustomers = [importedCustomers];
        }
      } catch (err) {
        // Wenn keine gültiges JSON, versuche CSV zu parsen
        // 1. BOM entfernen und Zeilenumbrüche normalisieren
        const cleanData = importData.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const lines = cleanData.split('\n').filter(line => line.trim() !== '');
        
        // Einfache CSV-Erkennung: Erste Zeile muss Header sein
        if (lines.length > 0) {
          // Erkennung des Trennzeichens (Semikolon oder Komma)
          const firstLine = lines[0];
          const semicolonCount = (firstLine.match(/;/g) || []).length;
          const commaCount = (firstLine.match(/,/g) || []).length;
          const delimiter = semicolonCount > commaCount ? ';' : ',';
          
          // Helper Funktion zum Parsen einer CSV Zeile (beachtet Quotes)
          const parseLine = (line: string): string[] => {
            const values: string[] = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              
              if (char === '"') {
                if (inQuotes && line[i + 1] === '"') {
                  current += '"';
                  i++; // Überspringe das nächste Quote
                } else {
                  inQuotes = !inQuotes;
                }
              } else if (char === delimiter && !inQuotes) {
                values.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            values.push(current.trim());
            return values;
          };

          // Header parsen und bereinigen
          const headers = parseLine(firstLine).map(h => h.replace(/^"|"$/g, '').trim());
          
          for (let i = 1; i < lines.length; i++) {
            if (!lines[i].trim()) continue;
            
            const values = parseLine(lines[i]);
            
            // Werte bereinigen (Quotes entfernen)
            const cleanValues = values.map(v => v.replace(/^"|"$/g, ''));
            
            const customer: Partial<Customer> = {};
            
            headers.forEach((header, index) => {
              // Sicherstellen, dass wir nicht auf undefined zugreifen
              if (index >= cleanValues.length) return;
              
              const value = cleanValues[index];
              if (value) {
                // Key-Generierung: Nur Buchstaben (auch Unicode) und Zahlen behalten
                // \p{L} matcht jeden Unicode-Buchstaben, \p{N} jede Zahl
                const normalizedKey = header.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
                
                // Mapping der normalisierten Keys zu den echten Property-Namen im Customer Interface
                let targetKey: keyof Customer | null = null;
                
                switch(normalizedKey) {
                    case 'companyname': targetKey = 'companyName'; break;
                    case 'personname': targetKey = 'personName'; break;
                    case 'name': targetKey = 'name'; break;
                    case 'street': targetKey = 'street'; break;
                    case 'zip': targetKey = 'zip'; break;
                    case 'plz': targetKey = 'zip'; break; // Synonym
                    case 'city': targetKey = 'city'; break;
                    case 'ort': targetKey = 'city'; break; // Synonym
                    case 'country': targetKey = 'country'; break;
                    case 'land': targetKey = 'country'; break; // Synonym
                    case 'customerid': targetKey = 'customerId'; break;
                    case 'kundennummer': targetKey = 'customerId'; break; // Synonym
                    case 'id': targetKey = 'id'; break;
                    case 'email': targetKey = 'email'; break;
                    case 'mail': targetKey = 'email'; break; // Synonym
                    case 'phone': targetKey = 'phone'; break;
                    case 'telefon': targetKey = 'phone'; break; // Synonym
                    case 'website': targetKey = 'website'; break;
                    case 'webseite': targetKey = 'website'; break; // Synonym
                    case 'street2': targetKey = 'street2'; break;
                    case 'reference': targetKey = 'reference'; break;
                    case 'referenz': targetKey = 'reference'; break; // Synonym
                    
                    // Rumänische Synonyme
                    case 'numelecompaniei': targetKey = 'companyName'; break;
                    case 'numecompanie': targetKey = 'companyName'; break;
                    case 'persoana': targetKey = 'personName'; break;
                    case 'nume': targetKey = 'personName'; break;
                    case 'persoanacontact': targetKey = 'personName'; break;
                    case 'strada': targetKey = 'street'; break;
                    case 'codpostal': targetKey = 'zip'; break;
                    case 'oras': targetKey = 'city'; break;
                    case 'tara': targetKey = 'country'; break;
                    case 'numarclient': targetKey = 'customerId'; break;
                    case 'nrclient': targetKey = 'customerId'; break;
                    case 'telefon': targetKey = 'phone'; break;
                    case 'siteweb': targetKey = 'website'; break;
                    case 'referinta': targetKey = 'reference'; break;

                    // Französische Synonyme
                    case 'nomdelentreprise': targetKey = 'companyName'; break;
                    case 'nomentreprise': targetKey = 'companyName'; break;
                    case 'personnedecontact': targetKey = 'personName'; break;
                    case 'nomdelapersonne': targetKey = 'personName'; break;
                    case 'rue': targetKey = 'street'; break;
                    case 'codepostal': targetKey = 'zip'; break;
                    case 'ville': targetKey = 'city'; break;
                    case 'pays': targetKey = 'country'; break;
                    case 'numeroclient': targetKey = 'customerId'; break;
                    case 'nclient': targetKey = 'customerId'; break;
                    case 'telephone': targetKey = 'phone'; break;
                    case 'site': targetKey = 'website'; break;
                    case 'siteweb': targetKey = 'website'; break;
                    case 'reference': targetKey = 'reference'; break;

                    // Polnische Synonyme
                    case 'nazwafirmy': targetKey = 'companyName'; break;
                    case 'osobakontaktowa': targetKey = 'personName'; break;
                    case 'osoba': targetKey = 'personName'; break;
                    case 'ulica': targetKey = 'street'; break;
                    case 'kodpocztowy': targetKey = 'zip'; break;
                    case 'miasto': targetKey = 'city'; break;
                    case 'kraj': targetKey = 'country'; break;
                    case 'numerklienta': targetKey = 'customerId'; break;
                    case 'nrklienta': targetKey = 'customerId'; break;
                    case 'telefon': targetKey = 'phone'; break;
                    case 'stronawww': targetKey = 'website'; break;
                    case 'stronainternetowa': targetKey = 'website'; break;
                    case 'referencja': targetKey = 'reference'; break;

                    // Russische Synonyme
                    case 'названиекомпании': targetKey = 'companyName'; break;
                    case 'контактноелицо': targetKey = 'personName'; break;
                    case 'улица': targetKey = 'street'; break;
                    case 'индекс': targetKey = 'zip'; break;
                    case 'почтовыйиндекс': targetKey = 'zip'; break;
                    case 'город': targetKey = 'city'; break;
                    case 'страна': targetKey = 'country'; break;
                    case 'номерклиента': targetKey = 'customerId'; break;
                    case 'email': targetKey = 'email'; break;
                    case 'телефон': targetKey = 'phone'; break;
                    case 'вебсайт': targetKey = 'website'; break;
                    case 'справка': targetKey = 'reference'; break;
                    
                    // Spanische Synonyme
                    case 'nombredeempresa': targetKey = 'companyName'; break;
                    case 'nombreempresa': targetKey = 'companyName'; break;
                    case 'personadecontacto': targetKey = 'personName'; break;
                    case 'calle': targetKey = 'street'; break;
                    case 'codigopostal': targetKey = 'zip'; break;
                    case 'ciudad': targetKey = 'city'; break;
                    case 'pais': targetKey = 'country'; break;
                    case 'numerocliente': targetKey = 'customerId'; break;
                    case 'ncliente': targetKey = 'customerId'; break;
                    case 'telefono': targetKey = 'phone'; break;
                    case 'sitioweb': targetKey = 'website'; break;
                    case 'referencia': targetKey = 'reference'; break;

                    // Italienische Synonyme
                    case 'nomeazienda': targetKey = 'companyName'; break;
                    case 'personadicontatto': targetKey = 'personName'; break;
                    case 'via': targetKey = 'street'; break;
                    case 'cap': targetKey = 'zip'; break;
                    case 'citta': targetKey = 'city'; break;
                    case 'paese': targetKey = 'country'; break;
                    case 'numerocliente': targetKey = 'customerId'; break;
                    case 'ncliente': targetKey = 'customerId'; break;
                    case 'telefono': targetKey = 'phone'; break;
                    case 'sitoweb': targetKey = 'website'; break;
                    case 'riferimento': targetKey = 'reference'; break;
                }

                if (targetKey) {
                   (customer as any)[targetKey] = value;
                }
              }
            });
            
            if (Object.keys(customer).length > 0) {
              importedCustomers.push(customer);
            }
          }
        }
      }
      
      if (importedCustomers.length === 0) {
        throw new Error('Keine gültigen Daten gefunden.');
      }

      // Batch-Verarbeitung für Firestore (max 500 Operationen pro Batch)
      const batchSize = 450; // Sicherheitsabstand zum Limit von 500
      const chunks = [];
      
      for (let i = 0; i < importedCustomers.length; i += batchSize) {
        chunks.push(importedCustomers.slice(i, i + batchSize));
      }

      let successCount = 0;
      const newLocalCustomers: Customer[] = [];

      for (const chunk of chunks) {
        const batch = writeBatch(db);
        
        for (const customer of chunk) {
          if (!customer.name && !customer.companyName && !customer.personName) continue;
          
          // Stelle sicher, dass das name-Feld korrekt gesetzt ist
          if (!customer.name && (customer.companyName || customer.personName)) {
            customer.name = customer.companyName || customer.personName || '';
          }
          
          const newCustomerRef = doc(collection(db, 'customers'));
          const customerData = {
            ...customer,
            userId: effectiveScopeUid,
            createdAt: new Date(),
            updatedAt: new Date(),
          };

          batch.set(newCustomerRef, customerData);
          
          // Für lokales Update vormerken
          newLocalCustomers.push({
            ...customerData,
            docId: newCustomerRef.id,
          } as Customer);
          
          successCount++;
        }
        
        try {
            await batch.commit();
        } catch (batchError) {
            console.error('Fehler beim Batch-Commit:', batchError);
            throw batchError;
        }
      }

      // Füge alle neuen Kunden zur lokalen Liste hinzu (einmaliges Update).
      // EIN Log-Eintrag für den gesamten Import, nicht N Stück.
      customerLog.recordCreate(newLocalCustomers);
      // Auch hier Upsert: der Batch-Commit kann lange laufen, eine parallel
      // eintreffende Lade-Antwort enthält die importierten Kunden dann schon.
      setCustomers(prev => upsertCustomers(prev, newLocalCustomers));
      
      setImportData('');
      setImportFileName(null);
      setIsImportModalOpen(false);
      toast.success(`${successCount} ${t('messages.importSuccess') || 'Kunden erfolgreich importiert'}`);
      
    } catch (err: any) {
      console.error('Fehler beim Import:', err);
      setError(mapErrorToUserMessage(err, t, { fallbackKey: 'messages.importError' }));
    } finally {
      setIsProcessing(false);
    }
  };

  // Spalten der Kundentabelle (M-E: DataTable ersetzt die vormals hier duplizierte
  // requestSort/getSortIndicator-Mechanik durch Column.sortValue + DataTable-initialSort).
  const customerColumns: Column<Customer>[] = [
    {
      id: 'name',
      header: t('table.headers.customer'),
      accessor: (customer) => {
        const primaryName = customer.companyName || customer.personName || customer.name || '';
        const showSecondaryContact = Boolean(customer.personName && customer.companyName);
        // DataTable's own truncate wrapper only auto-derives a `title` for STRING
        // accessors (§5.5 doc-comment: "Non-string accessors are still clamped but
        // carry no title"). This accessor returns JSX (2. Zeile Ansprechpartner),
        // so the hover-langform must be set explicitly here — otherwise a clipped
        // long customer name has no way to reveal its full text (M-E-Nachtrag).
        const fullNameTitle = showSecondaryContact
          ? `${primaryName} (${customer.personName})`
          : primaryName;
        return (
          <div title={fullNameTitle || undefined}>
            <div className="text-sm font-medium text-primary">{primaryName}</div>
            {showSecondaryContact && (
              <div className="text-sm text-muted">{customer.personName}</div>
            )}
          </div>
        );
      },
      sortValue: (customer) => customer.companyName || customer.personName || customer.name || '',
      truncate: 'max-w-[16rem]',
    },
    {
      id: 'location',
      header: t('table.headers.location'),
      accessor: (customer) => (
        <div className="text-sm text-secondary">{customer.zip} {customer.city}</div>
      ),
      sortValue: (customer) => customer.city || '',
    },
    {
      id: 'country',
      header: t('table.headers.country'),
      accessor: (customer) => <div className="text-sm text-secondary">{customer.country}</div>,
      sortValue: (customer) => customer.country || '',
      hideBelow: 'md',
    },
    {
      id: 'customerId',
      header: t('table.headers.customerNumber'),
      accessor: (customer) => <div className="text-sm text-secondary">{customer.customerId || '-'}</div>,
      sortValue: (customer) => customer.customerId || '',
      hideBelow: 'sm',
    },
    // Aktionen — M11b-3: für advisor (read-only) ausgeblendet
    {
      id: 'actions',
      header: t('table.headers.actions'),
      align: 'right',
      sticky: 'right',
      accessor: (customer) =>
        !isReadOnly ? (
          <div className="flex items-center justify-end gap-1" onClick={(event) => event.stopPropagation()}>
            <RowAction label={t('edit')} icon={<IconEdit />} variant="accent" onClick={() => handleEdit(customer)} />
            <RowAction label={t('delete')} icon={<IconDelete />} variant="danger" onClick={() => handleDelete(customer)} />
          </div>
        ) : null,
    },
  ];

  return (
    <div className="bg-surface text-primary min-h-screen">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <h1 className="text-2xl font-semibold text-accent-ink mb-4 md:mb-0">{t('title')}</h1>
            <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-3 w-full md:w-auto">
              {/* Suchfeld */}
              <div className="relative flex-grow md:max-w-md">
                <input
                  type="text"
                  placeholder={t('search')}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-hairline bg-surface-raised text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              
              {/* Import + Neuer Kunde — M11b-3: für advisor (read-only) ausgeblendet */}
              {!isReadOnly && (
              <>
              {/* Import-Button */}
              <button
                className="bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => setIsImportModalOpen(true)}
              >
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                                      {t('import.title')}
                </span>
              </button>
              
              {/* Neuer Kunde-Button */}
              <button
                className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ml-3"
                onClick={() => {
                  // Leeres Kundenobjekt erstellen
                  const tempDocId = Date.now().toString(); // Nur für React-Komponente
                  const newCustomer: Customer = {
                    docId: tempDocId, // Temporäre Document-ID für React-Komponente
                    name: '',
                    companyName: '',
                    personName: '',
                    street: '',
                    zip: '',
                    city: '',
                    country: 'DE', // Default-Wert
                    street2: '',
                    reference: '',
                    customerId: '',
                    email: '',
                    phone: '',
                    website: '',
                    createdAt: new Date(),
                    updatedAt: new Date()
                  };
                  setEditingCustomer(newCustomer);
                  // Das Formular verwendet alle Felder des newCustomer Objekts
                  setFormData({ ...newCustomer });
                  setIsEditModalOpen(true);
                }}
              >
                <span className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  {t('newCustomer')}
                </span>
              </button>
              </>
              )}
            </div>
          </div>
          
          {/* Error Message */}
          {error && (
            <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] text-[var(--tone-danger-fg)] px-4 py-3 rounded-md mb-4">
              {error}
            </div>
          )}
          
          {/* Kundentabelle */}
          {loading ? (
            <div className="bg-surface-raised rounded-xl overflow-hidden shadow-lg border border-hairline flex justify-center items-center p-8">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : (
            <DataTable<Customer>
              columns={customerColumns}
              rows={filteredCustomers}
              rowKey={(customer) => customer.docId}
              density="compact"
              initialSort={{ id: 'name', dir: 'asc' }}
              empty={
                <div className="text-center text-secondary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="text-lg">{t('noCustomersFound')}</p>
                  <p className="text-muted mt-2">{t('automaticallyAdded')}</p>
                </div>
              }
            />
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-surface-raised rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-accent-ink">{t('editCustomer')}</h2>
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-muted hover:text-primary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleSaveCustomer}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Kundennummer */}
                  <div>
                    <label htmlFor="customerId" className="block text-sm font-medium text-muted mb-1">{t('fields.customerId')}</label>
                    <input
                      type="text"
                      id="customerId"
                      name="customerId"
                      value={formData.customerId || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  {/* Käufer-ID - NEU */}
                  <div>
                    <label htmlFor="id" className="block text-sm font-medium text-muted mb-1">{t('fields.buyerId')}</label>
                    <input
                      type="text"
                      id="id"
                      name="id"
                      value={formData.id || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={t('placeholders.buyerId')}
                    />
                    <p className="mt-1 text-xs text-faint">{t('fields.buyerIdDescription')}</p>
                  </div>
                  
                  {/* Art des Kunden - Firma oder Person (wird später genutzt) */}
                  <div className="md:col-span-2 mb-4">
                    <div className="flex flex-col md:flex-row md:items-center">
                      <label className="block text-sm font-medium text-muted mb-1 md:mb-0 md:mr-4">{t('fields.customerType')}</label>
                      <div className="flex space-x-4">
                        <span className="text-secondary">{t('fields.customerTypeDescription')}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Firmenname */}
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-muted mb-1">{t('fields.companyName')}</label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  {/* Person */}
                  <div>
                    <label htmlFor="personName" className="block text-sm font-medium text-muted mb-1">{t('fields.personName')}</label>
                    <input
                      type="text"
                      id="personName"
                      name="personName"
                      value={formData.personName || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  {/* Straße */}
                  <div className="md:col-span-2">
                    <label htmlFor="street" className="block text-sm font-medium text-muted mb-1">{t('fields.street')}</label>
                    <input
                      type="text"
                      id="street"
                      name="street"
                      value={formData.street || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  
                  {/* Straße 2 */}
                  <div className="md:col-span-2">
                    <label htmlFor="street2" className="block text-sm font-medium text-muted mb-1">{t('fields.street2')}</label>
                    <input
                      type="text"
                      id="street2"
                      name="street2"
                      value={formData.street2 || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  {/* PLZ */}
                  <div>
                    <label htmlFor="zip" className="block text-sm font-medium text-muted mb-1">{t('fields.zip')}</label>
                    <input
                      type="text"
                      id="zip"
                      name="zip"
                      value={formData.zip || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  
                  {/* Stadt */}
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-muted mb-1">{t('fields.city')}</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                  
                  {/* Land */}
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-muted mb-1">{t('fields.country')}</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                    <p className="text-xs text-faint mt-1">{t('fields.countryDescription')}</p>
                  </div>
                  
                  {/* Referenz */}
                  <div>
                    <label htmlFor="reference" className="block text-sm font-medium text-muted mb-1">{t('fields.reference')}</label>
                    <input
                      type="text"
                      id="reference"
                      name="reference"
                      value={formData.reference || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  {/* Kommunikation - Email */}
                  <div className="md:col-span-2">
                    <label htmlFor="email" className="block text-sm font-medium text-muted mb-1">
                      {t('fields.email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={t('placeholders.email')}
                    />
                  </div>
                  
                  {/* Kommunikation - Telefon */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-muted mb-1">
                      {t('fields.phone')}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={t('placeholders.phone')}
                    />
                  </div>
                  
                  {/* Kommunikation - Website */}
                  <div>
                    <label htmlFor="website" className="block text-sm font-medium text-muted mb-1">
                      {t('fields.website')}
                    </label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={formData.website || ''}
                      onChange={handleFormChange}
                      className="w-full p-2 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={t('placeholders.website')}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 bg-surface-inset hover:bg-surface-hover text-primary rounded-md"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {isProcessing ? t('processing') : t('save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-surface-raised rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-accent-ink">{t('import.title')}</h2>
                <button
                  onClick={() => setIsImportModalOpen(false)}
                  className="text-muted hover:text-primary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-secondary mb-2">{t('import.description')}</p>
                
                <div className="flex space-x-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setUseFileUpload(true)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      useFileUpload
                        ? 'bg-indigo-600 text-[#0C1322]'
                        : 'bg-surface-inset text-secondary hover:bg-surface-hover'
                    }`}
                  >
                    {t('import.uploadFile') || 'Datei hochladen'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseFileUpload(false)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      !useFileUpload
                        ? 'bg-indigo-600 text-[#0C1322]'
                        : 'bg-surface-inset text-secondary hover:bg-surface-hover'
                    }`}
                  >
                    {t('import.directInput') || 'Direkte Eingabe'}
                  </button>
                </div>

                {/* Instructions - Always visible */}
                <div className="mb-6 bg-[var(--tone-info-bg)] border border-[var(--tone-info-border)] p-4 rounded-lg">
                    <h3 className="text-[var(--tone-info-fg)] font-medium mb-2 text-sm flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {t('import.instructionsTitle')}
                    </h3>
                    <ul className="list-disc list-inside text-xs text-secondary space-y-1 ml-1">
                      <li><span dangerouslySetInnerHTML={{ __html: t('import.instruction1') }} /></li>
                      <li>
                        {t('import.instruction2')}<br/>
                        <span className="font-mono text-accent-ink ml-4 block mt-1">
                          {t('import.supportedColumns')}
                        </span>
                      </li>
                      <li>{t('import.instruction3')}</li>
                    </ul>
                    
                    <details className="text-xs mt-3">
                        <summary className="text-accent-ink cursor-pointer hover:text-accent-ink transition-colors">{t('import.showExamples')}</summary>
                        
                        <div className="mt-3 grid grid-cols-1 gap-3">
                          <div className="bg-surface p-3 rounded border border-hairline">
                            <h4 className="text-xs font-semibold text-muted mb-2">{t('import.excelCsvFormat')}</h4>
                            <pre className="text-xs text-faint overflow-x-auto whitespace-pre-wrap font-mono">
                              {t('import.csvExampleContent')}
                            </pre>
                            <p className="text-[10px] text-gray-600 mt-1 italic">{t('import.excelTip')}</p>
                          </div>
                        </div>
                    </details>
                </div>

                {useFileUpload ? (
                  <div className="mb-4">
                    <label htmlFor="importFile" className="block text-secondary text-sm font-medium mb-2">
                      {t('import.selectFile')}
                    </label>
                    <div className="flex items-center justify-center w-full">
                        <label htmlFor="importFile" className="flex flex-col items-center justify-center w-full h-32 border-2 border-strong border-dashed rounded-lg cursor-pointer bg-surface-raised hover:bg-surface-inset transition-colors">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                {importFileName ? (
                                  <>
                                    <svg className="w-10 h-10 mb-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                    <p className="mb-2 text-sm text-primary font-semibold">{importFileName}</p>
                                    <p className="text-xs text-muted">{t('import.clickToSelect') || 'Klicken zum Ändern'}</p>
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-8 h-8 mb-3 text-muted" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                                    </svg>
                                    <p className="mb-2 text-sm text-muted"><span className="font-semibold">{t('import.clickToSelect')}</span> {t('import.orDragDrop')}</p>
                                    <p className="text-xs text-faint">{t('import.fileConstraints')}</p>
                                  </>
                                )}
                            </div>
                            <input id="importFile" type="file" className="hidden" accept=".json,.csv" onChange={handleFileChange} />
                        </label>
                    </div>
                  </div>
                ) : (
                  <>
                    <label htmlFor="importData" className="block text-sm font-medium text-muted mb-1">{t('import.pasteData')}</label>
                    <textarea
                      id="importData"
                      rows={8}
                      value={importData}
                      onChange={(e) => setImportData(e.target.value)}
                      className="w-full p-3 rounded-md border border-hairline bg-surface-raised text-primary focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                      placeholder={t('import.pastePlaceholder')}
                    ></textarea>
                  </>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsImportModalOpen(false)}
                  className="px-4 py-2 bg-surface-inset hover:bg-surface-hover text-primary rounded-md"
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleImport}
                  disabled={isProcessing || !importData.trim()}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-[#0C1322] font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {isProcessing ? t('importing') : t('import.title')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Lösch-Bestätigungsdialog */}
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        title={t('messages.deleteTitle')}
        message={t('messages.deleteConfirm', { name: customerToDelete?.name || '' })}
        options={[
          {
            label: t('cancel'),
            action: () => {
              setDeleteDialogOpen(false);
              setCustomerToDelete(null);
            },
            variant: 'secondary',
          },
          {
            label: t('delete'),
            action: confirmDelete,
            variant: 'danger',
          },
        ]}
        onClose={() => {
          setDeleteDialogOpen(false);
          setCustomerToDelete(null);
        }}
      />
    </div>
  );
};

export default CustomerManagementPage; 